import { SPORTS } from '../../data/constants';
import { legacySportKey } from '../../data/sports';
import { StatCards, SectionCard, HBar, WeeklyBarChart, EmptyState, last8Weeks } from './shared';

const STRIKING_SPORTS = ['kickboxing', 'boxing', 'muaythai', 'mma'];

export default function StrikingAnalysis({ records }) {
    if (!records.length) return <EmptyState text="타격 기록이 없어요." />;

    const totalMin = records.reduce((a, r) => a + (+r.duration || 0), 0);
    const sparringRecs = records.filter(r => r.sparring);
    const totalSparRounds = records.reduce((a, r) => a + (+r.sparringRounds || 0), 0);

    // 종목별 분포
    const sportCounts = {};
    records.forEach(r => {
        const k = legacySportKey(r);
        sportCounts[k] = (sportCounts[k] || 0) + 1;
    });
    const maxSport = Math.max(...Object.values(sportCounts), 1);

    // 드릴 타입 분포
    const drillMap = {};
    let totalDrillMin = 0;
    records.forEach(r => {
        (r.strikingDrills || []).filter(d => d.type).forEach(d => {
            drillMap[d.type] = (drillMap[d.type] || 0) + 1;
            totalDrillMin += +d.minutes || 0;
        });
    });
    const topDrills = Object.entries(drillMap).sort((a, b) => b[1] - a[1]).slice(0, 6);
    const maxDrill = topDrills[0]?.[1] || 1;

    // 기술 카테고리 분포
    const catMap = {};
    records.forEach(r => {
        (r.techniques || []).filter(t => t.name || t.category).forEach(t => {
            const cat = t.category || '기타';
            catMap[cat] = (catMap[cat] || 0) + 1;
        });
    });
    const topCats = Object.entries(catMap).sort((a, b) => b[1] - a[1]);
    const maxCat = topCats[0]?.[1] || 1;

    // 카테고리 색상
    const CAT_COLORS = {
        '펀치': '#ff7c5c', '킥': '#ffa44f', '무릎/엘보': '#ff5c8a',
        '클린치': '#9b8cff', '디펜스': '#5ba4f5', '기타': 'var(--text3)',
    };

    // 주간 스파링 라운드
    const weeklySpar = last8Weeks(records, recs => recs.reduce((a, r) => a + (+r.sparringRounds || 0), 0));

    return (
        <>
            <StatCards items={[
                { val: `${records.length}회`, label: '총 세션' },
                { val: `${Math.round(totalMin / 60)}h`, label: '총 시간' },
                { val: `${sparringRecs.length}회`, label: '스파링 세션' },
                { val: `${totalSparRounds}R`, label: '총 스파링 라운드' },
            ]} />

            <SectionCard title="종목 분포" desc="종목별 세션 수">
                {STRIKING_SPORTS.filter(k => sportCounts[k]).map(k => (
                    <HBar key={k} label={SPORTS[k]?.name || k} value={sportCounts[k]} max={maxSport} color={SPORTS[k]?.color || '#ff7c5c'} suffix="회" />
                ))}
            </SectionCard>

            {topDrills.length > 0 && (
                <SectionCard title="훈련 구성" desc={`드릴 타입 분포 · 총 ${Math.round(totalDrillMin)}분 기록`}>
                    {topDrills.map(([type, cnt]) => (
                        <HBar key={type} label={type} value={cnt} max={maxDrill} color="#ff7c5c" suffix="세션" />
                    ))}
                </SectionCard>
            )}

            {topCats.length > 0 && (
                <SectionCard title="기술 카테고리" desc="어떤 기술을 많이 연습했는지">
                    {topCats.map(([cat, cnt]) => (
                        <HBar key={cat} label={cat} value={cnt} max={maxCat} color={CAT_COLORS[cat] || '#ff7c5c'} suffix="회" />
                    ))}
                </SectionCard>
            )}

            <SectionCard title="스파링 추이" desc="주별 스파링 라운드 수">
                <WeeklyBarChart data={weeklySpar} label="주간 스파링 라운드" color="#ff7c5c" />
                <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: 'var(--text3)' }}>
                    <span>스파링 비율 {records.length ? Math.round(sparringRecs.length / records.length * 100) : 0}%</span>
                    <span>세션 평균 {sparringRecs.length ? (totalSparRounds / sparringRecs.length).toFixed(1) : 0}R</span>
                </div>
            </SectionCard>
        </>
    );
}
